import { useClient } from '@base/api/useClient'
import { ref } from 'vue'
import { ValidationError } from '__generated__/globalTypes'
import { RequestConfig } from './types/viewRequest'
import { UseViewStateReturn } from './useViewState'
import { F } from 'ts-toolbelt'

export enum RequestState {
  UNKNOWN,
  SUCCESS,
  INPUT_ERROR,
  UNAUTHENTICATED_ERROR,
  UNAUTHORIZED_ERROR,
  SERVER_ERROR,
  LOADING
}

export function useViewRequest<
  Output,
  Input extends Record<string, unknown> = Record<string, unknown>,
  S extends UseViewStateReturn<Record<string, unknown>> = UseViewStateReturn<
    Record<string, unknown>
  >
>(config: F.Narrow<RequestConfig<Input, S['state']>>, stateObj: S) {
  const client = useClient()
  const { state } = stateObj

  const [
    loading,
    inputError,
    unauthenticatedError,
    unauthorizedError,
    serverError,
    success
  ] = [ref(false), ref(false), ref(false), ref(false), ref(false), ref(false)]

  const request = async <
    SuccessType,
    InputError extends Record<string, string | ValidationError[] | null>
  >(
    reqCallbacks: {
      success?: (output: Output) => SuccessType | null
      inputError?: (output: Output) => InputError | null
      unauthenticatedError?: (output: Output) => boolean
      unauthorizedError?: (output: Output) => boolean
    },
    reqConfig: {
      onlyUpdated?: boolean
      excludeFromOnlyUpdated?: (keyof typeof state)[]
      skipResetUpdatedOnSuccess?: boolean // Skips reseting state field updated after successful request when only updated is true
    } = {
      onlyUpdated: false
    }
  ): Promise<
    [RequestState, SuccessType | InputError | Output | null, Output | null]
  > => {
    if (loading.value) return [RequestState.LOADING, null, null]
    loading.value = true
    success.value = false
    inputError.value = false
    serverError.value = false
    unauthenticatedError.value = false
    unauthorizedError.value = false

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const variables: { [key: string]: any } = {}
    const varCfg = config.variableConfig

    if (varCfg) {
      for (const [varKey, fieldObj] of Object.entries(varCfg)) {
        let variation = 0
        let fieldName: keyof typeof state = ''

        if (Array.isArray(fieldObj)) {
          fieldName = fieldObj[0]
          variation = fieldObj[1]
        } else if (typeof fieldObj === 'function') {
          variables[varKey] = fieldObj()
        } else {
          fieldName = fieldObj
        }

        if (state[fieldName] === undefined) continue

        const mappedField = state[fieldName]

        let shouldUpdate = false

        if (reqConfig.onlyUpdated) {
          shouldUpdate =
            mappedField.updated.value ||
            (reqConfig.excludeFromOnlyUpdated !== undefined &&
              reqConfig.excludeFromOnlyUpdated.includes(fieldName))
        } else {
          shouldUpdate = true
        }

        if (shouldUpdate) {
          variables[varKey] = await mappedField.toRequestVariable(variation)
        }
      }
    }

    try {
      let data: Output | null | undefined

      if (config.type === 'mutation') {
        data = (
          await client.mutate<Output>({
            mutation: config.request,
            fetchPolicy: config.fetchPolicy || 'no-cache',
            variables: { ...config.staticVariables, ...variables }
          })
        ).data
      } else {
        data = (
          await client.query<Output>({
            query: config.request,
            fetchPolicy: config.fetchPolicy || 'cache-first',
            variables
          })
        ).data
      }

      // check if data exists
      if (!data) {
        serverError.value = true
        return [RequestState.SERVER_ERROR, null, null]
      }

      // run success path and return
      if (reqCallbacks.success) {
        const successData = reqCallbacks.success(data)
        if (successData !== null) {
          success.value = true

          if (reqConfig.onlyUpdated) stateObj.resetFieldsUpdated()
          // TODO attempt to populate

          return [RequestState.SUCCESS, successData, data]
        }
      }

      if (reqCallbacks.inputError) {
        const errors = reqCallbacks.inputError(data)
        if (errors !== null) {
          inputError.value = true

          if (config.errorConfig) {
            for (const [error, stateKeys] of Object.entries(
              config.errorConfig
            )) {
              // console.log(errors)

              if (!errors[error]) continue
              const errorMessage = errors[error]?.toString() || ''

              if (Array.isArray(stateKeys)) {
                for (const stateKey of stateKeys) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  state[stateKey].error.value = errorMessage
                }
              } else if (typeof stateKeys === 'string') {
                state[stateKeys].error.value = errorMessage
              }
            }
          }

          return [RequestState.INPUT_ERROR, errors, data]
        }
      }

      if (reqCallbacks.unauthenticatedError) {
        const unauthenticated = reqCallbacks.unauthenticatedError(data)
        if (unauthenticated !== null) {
          return [RequestState.UNAUTHENTICATED_ERROR, null, data]
        }
      }

      if (reqCallbacks.unauthorizedError) {
        const unauthorized = reqCallbacks.unauthorizedError(data)
        if (unauthorized !== null) {
          return [RequestState.UNAUTHORIZED_ERROR, null, data]
        }
      }

      if (
        reqCallbacks.success ||
        reqCallbacks.inputError ||
        reqCallbacks.unauthenticatedError ||
        reqCallbacks.unauthorizedError
      ) {
        serverError.value = true
        return [RequestState.SERVER_ERROR, null, data]
      }

      return [RequestState.UNKNOWN, null, data]
    } catch (error) {
      console.error(error)
      serverError.value = true
    } finally {
      loading.value = false
    }

    return [RequestState.UNKNOWN, null, null]
  }

  return {
    loading,
    success,
    inputError,
    serverError,
    request
  }
}
